/**
 * src/core/event-emitter.ts
 *
 * Isomorphic, Browser-Safe EventEmitter.
 * Zero-dependency event dispatcher — canonical location for cross-layer use.
 * src/shell/utils/event-emitter.ts re-exports from here for backwards compatibility.
 */

type Listener = (...args: any[]) => void;

export class EventEmitter {
  private events: Map<string, Set<Listener>> = new Map();

  public on(event: string, listener: Listener): this {
    if (!this.events.has(event)) {
      this.events.set(event, new Set());
    }
    this.events.get(event)!.add(listener);
    return this;
  }

  public off(event: string, listener: Listener): this {
    const listeners = this.events.get(event);
    if (listeners) {
      listeners.delete(listener);
      if (listeners.size === 0) {
        this.events.delete(event);
      }
    }
    return this;
  }

  public emit(event: string, ...args: any[]): boolean {
    const listeners = this.events.get(event);
    if (!listeners || listeners.size === 0) {
      return false;
    }
    listeners.forEach((listener) => {
      try {
        listener(...args);
      } catch (err) {
        console.error(`[EventEmitter] Error in listener for event "${event}":`, err);
      }
    });
    return true;
  }

  public removeAllListeners(event?: string): this {
    if (event) {
      this.events.delete(event);
    } else {
      this.events.clear();
    }
    return this;
  }
}
